// Authentication-related utility functions for user management
import {
  getAuth,
  onAuthStateChanged,
  User as FirebaseUser,
} from "firebase/auth"
import { doc, getDoc } from "firebase/firestore"
import { db } from "../../firebase"
import { USERS_COLLECTION } from "../constants"
// Removed: import { updateUser } from "./userOperations"
import type { UserDataInput } from '@/schemas/user'; // For the new parameter type

// Get current authenticated user from Firebase
export const getCurrentAuthUser = (): FirebaseUser | null => {
  const auth = getAuth()
  return auth.currentUser
}

// Define a more specific return type
interface UserData {
  id: string
  userId: string
  email?: string | null
  firstName?: string
  lastName?: string
  avatarUrl?: string
  lastSeen?: Date
  role?: string
  phone?: string
  avatarInitials?: string
  createdAt?: Date
  updatedAt?: Date
  // Use unknown instead of any for better type safety
  [key: string]: string | number | boolean | Date | null | undefined | unknown
}

// Get current user data - exported for use in other modules
export const getCurrentUserFromAuth = async (): Promise<UserData | null> => {
  const firebaseUser = getCurrentAuthUser()
  if (!firebaseUser) return null

  try {
    const userRef = doc(db, USERS_COLLECTION, firebaseUser.uid)
    const userDoc = await getDoc(userRef)

    if (userDoc.exists()) {
      return {
        ...userDoc.data(),
        id: firebaseUser.uid,
        userId: firebaseUser.uid,
      }
    } else {
      return {
        id: firebaseUser.uid,
        userId: firebaseUser.uid,
        email: firebaseUser.email,
        firstName: firebaseUser.displayName?.split(" ")[0] || "",
        lastName: firebaseUser.displayName?.split(" ").slice(1).join(" ") || "",
        avatarUrl: firebaseUser.photoURL || "",
        lastSeen: new Date(),
      }
    }
  } catch (error) {
    console.error("Error fetching current user data")
    return null
  }
}

// Initialize user data when a user signs in
export const initializeUserData = async (
  firebaseUser: FirebaseUser,
  // Added updateUserFromHook parameter
  updateUserFromHook: (userId: string, data: Partial<UserDataInput>) => Promise<void> 
): Promise<void> => {
  if (!firebaseUser) return

  try {
    // Check if user document exists
    const userRef = doc(db, USERS_COLLECTION, firebaseUser.uid)
    const userDoc = await getDoc(userRef)

    if (!userDoc.exists()) {
      // Create a new user document if it doesn't exist
      console.log("initializeUserData: user doc does not exist")
      const userData = {
        id: firebaseUser.uid,
        userId: firebaseUser.uid,
        email: firebaseUser.email || "",
        firstName: firebaseUser.displayName?.split(" ")[0] || "",
        lastName: firebaseUser.displayName?.split(" ").slice(1).join(" ") || "",
        phone: firebaseUser.phoneNumber || "",
        avatarUrl: firebaseUser.photoURL || "",
        avatarInitials: firebaseUser.displayName
          ? firebaseUser.displayName
              .split(" ")
              .map((n) => n[0])
              .join("")
              .toUpperCase()
          : firebaseUser.email?.substring(0, 2).toUpperCase() || "U",
        lastSeen: new Date(),
        createdAt: new Date(),
        portfolios: [], // Initialize as empty array
        businesses: [], // Initialize as empty array
      }

      await updateUserFromHook(firebaseUser.uid, userData) // Changed to updateUserFromHook
    } else {
      // Update the existing user document with the latest auth data
      const existingData = userDoc.data()

      // Only update fields that may have changed in Firebase Auth
      // CRITICAL: Do NOT update the role field here to preserve existing roles
      const updates = {
        id: firebaseUser.uid,
        userId: firebaseUser.uid,
        email: firebaseUser.email || existingData.email || "",
        firstName:
          firebaseUser.displayName?.split(" ")[0] ||
          existingData.firstName ||
          "",
        lastName:
          firebaseUser.displayName?.split(" ").slice(1).join(" ") ||
          existingData.lastName ||
          "",
        phone: firebaseUser.phoneNumber || existingData.phone || "",
        avatarUrl: firebaseUser.photoURL || existingData.avatarUrl || "",
        lastSeen: new Date(),
      }

      await updateUserFromHook(firebaseUser.uid, updates) // Changed to updateUserFromHook
    }
  } catch (error) {
    console.error("Error initializing user data")
  }
}

// Set up auth state listener
export const setupAuthListener = (
  callback: (user: FirebaseUser | null) => void
): (() => void) => {
  const auth = getAuth()
  return onAuthStateChanged(auth, callback)
}
